import axios from 'axios';

import { get, post, put, patch, del } from '..';
import {
  ICreateCustomerProgress,
  ICreateCustomerRequest,
  ICustomer,
  ICustomerPagination,
  ICustomerUpdatePayload,
  ILicense,
  ILicenseExtended,
  IScriveLicensePeriod,
  IScriveLicensePeriodCreatePayload,
  IZoined,
  IZoinedExtended,
  ICustomersForDeletion,
  ISysAdminTokens,
} from '../../interfaces/customer';
import { DeletedCustomerQueueStatus } from '@/constants/customer-deletion';
import { buildUrl } from '../../utils';

export const getDeletedCustomersQueue = (status?: DeletedCustomerQueueStatus) =>
  get(
    buildUrl(
      `/v1/customer/customers-for-deletion${status !== undefined ? `?status=${status}` : ''}`,
    ),
  );

export const getRemovableCustomers = () =>
  get(buildUrl('/v1/customer/customers-for-deletion'));

export const deleteCustomer = (customers: ICustomersForDeletion[]) =>
  post(buildUrl('/v1/customer/add-customers-for-deletion'), customers);

export const updateCustomerDeleteBy = (customerId: number, deleteBy: string) =>
  put(buildUrl('/v1/customer/customers-for-deletion'), {
    id: customerId,
    deleteBy,
  });

export const undoDeleteCustomer = (customerId: number) =>
  del(buildUrl(`/v1/customer/customers-for-deletion/${customerId}`));

export const getCustomerCreationsInProgress = () =>
  get(buildUrl('/v1/customer/customer-creation-request'));

export const getCustomerCreationProgressApi = (id: string) =>
  get(buildUrl(`/v1/customer/customer-creation-request/${id}`));

export const createCustomerApi = (
  customer: ICreateCustomerRequest,
  tokens?: ISysAdminTokens,
  targetGateway?: string,
) => {
  const headers = tokens && {
    'X-Sys-Admin-Token': tokens.target,
    'X-Source-Sys-Admin-Token': tokens.source,
  };
  if (!targetGateway) {
    return post(buildUrl('/v1/customer/customer-creation'), customer, headers);
  }
  // Another environment's gateway, where this back office has no session: the target token alone
  // authenticates the request. Not through post(), which answers a 401 by sending the admin to
  // this environment's login - here a 401 means the target environment refused the token.
  return axios
    .post<ICreateCustomerProgress>(
      `${targetGateway}/v1/customer/customer-creation`,
      customer,
      { headers },
    )
    .then((res) => res.data);
};

export const getLicenseApi = (domainId: string) =>
  get<ILicenseExtended>(buildUrl(`/v1/customer/domain/${domainId}/license`));

export const updateLicenseApi = (domainId: string, license: ILicense) =>
  put<ILicenseExtended>(
    buildUrl(`/v1/customer/domain/${domainId}/license`),
    license,
  );

export const createLicenseApi = (domainId: string, license: ILicense) =>
  post<ILicenseExtended>(
    buildUrl(`/v1/customer/domain/${domainId}/license`),
    license,
  );

export const getScriveLicensesApi = (domainId: string, showDeleted = false) =>
  get<IScriveLicensePeriod[]>(
    buildUrl(
      `/v1/customer/domain/${domainId}/scrive-license-period${showDeleted ? '?showDeleted=true' : ''}`,
    ),
  );

export const createScriveLicensePeriodApi = (
  domainId: string,
  payload: IScriveLicensePeriodCreatePayload,
) =>
  post<IScriveLicensePeriod>(
    buildUrl(`/v1/customer/domain/${domainId}/scrive-license-period`),
    payload,
  );

export const updateScriveLicensePeriodApi = (
  domainId: string,
  id: string,
  payload: IScriveLicensePeriodCreatePayload,
) =>
  put<IScriveLicensePeriod>(
    buildUrl(`/v1/customer/domain/${domainId}/scrive-license-period/${id}`),
    payload,
  );

export const deleteScriveLicensePeriodApi = (domainId: string, id: string) =>
  del(buildUrl(`/v1/customer/domain/${domainId}/scrive-license-period/${id}`));

export const getZoinedApi = (domainId: string) =>
  get<IZoinedExtended>(buildUrl(`/v1/customer/domain/${domainId}/zoined`));

export const createZoinedApi = (domainId: string, zoined: IZoined) =>
  post<ILicenseExtended>(buildUrl(`/v1/customer/domain/${domainId}/zoined`), {
    zoinedEnabled: true,
    ...zoined,
  });

export const updateZoinedApi = (domainId: string, zoined: IZoined) =>
  put<IZoinedExtended>(buildUrl(`/v1/customer/domain/${domainId}/zoined`), {
    zoinedEnabled: true,
    ...zoined,
  });

export const getPaginatedCustomersApi = (queryParams?: string) =>
  get<ICustomerPagination>(buildUrl(`/v1/customer/domain${queryParams ?? ''}`));

export const getCustomerApi = (domainId: string) =>
  get<ICustomer>(buildUrl(`/v1/customer/domain/${domainId}`));

export const updateCustomerApi = (
  domainId: string,
  payload: ICustomerUpdatePayload,
) => put<ICustomer>(buildUrl(`/v1/customer/domain/${domainId}`), payload);

export const reprovisionCustomerApi = (domainId: string) =>
  patch(buildUrl(`/v1/messenger-api/domain/${domainId}`), {});
